// managers/pdar/PdarsManager.ts
import type { Map as MapType, GeoJSONSource } from "maplibre-gl";
import type { MapEventBus } from "../../core/MapEventBus";
import { SOURCES } from "../../sources";
import { PdarsSelection } from "./PdarsSelection";
import { PdarsRenderer } from "./PdarsRenderer";
import type {
  PdarPayload,
  Pdars,
  PdarPlatform,
  PdarTargets,
} from "../../types/pdar";
import { getAltitudeInFeet } from "@/client/lib/getAltitudeInFeet";
import { getSpeedInKnots } from "@/client/lib/getSpeedInKnots";
import { getHeadingInDegrees } from "@/client/lib/getHeadingInDegrees";

const STALE_AFTER_MS = 60 * 1000;
const DEFAULT_AOA_LINE_LENGTH_M = 3000; // Default AoA line length in metres

// Wraps any degree value (including negatives, e.g. from the -180..180
// calibration slider) into the 0..360 range expected by icon-rotate and
// bearing math.
function to360(deg: number): number {
  return ((deg % 360) + 360) % 360;
}

export class PdarsManager {
  private platformCache: PdarPlatform | null = null;
  private targetCache = new Map<string, PdarTargets>();
  private pdarCache = new Map<string, Pdars>();

  private pendingUpdates = new Set<string>();
  private selection: PdarsSelection;
  private renderer: PdarsRenderer;
  private animationFrameId: number | null = null;
  private currentlySelectedIds = new Set<string>();

  // Client-side-only calibration offset (degrees, any range) per sensor,
  // set via the UI (see usePdarSelection.setOrientationOffset). PDARs are
  // installed assuming true-north facing, but real installs can't always
  // guarantee that — this lets a user correct for it visually without
  // touching the wire `orientation` value the sensor itself reports. Never
  // published back over the websocket; purely a rendering adjustment.
  private orientationOffsets = new Map<string, number>();

  constructor(
    private map: MapType,
    private eventBus: MapEventBus,
  ) {
    this.selection = new PdarsSelection(eventBus);
    this.renderer = new PdarsRenderer(map);

    this.setupListeners();
    this.startUpdateLoop();
  }

  private setupListeners() {
    this.eventBus.on("pdar:selected", ({ pdar_id }) => {
      if (!pdar_id) return;
      this.currentlySelectedIds.add(pdar_id);
      this.pendingUpdates.add(pdar_id);
      // Covers selection changes that originate from PdarsSelection directly
      // (map clicks via pdar:clicked, or a hook's toggle() call) rather than
      // through selectPdar()/deselectPdar() — without this, React consumers
      // of usePdarSelection would only refresh on the next data batch.
      this.notifySelectedPdarChanged();
    });

    this.eventBus.on("pdar:deselected", ({ pdar_id }) => {
      if (!pdar_id) return;
      this.currentlySelectedIds.delete(pdar_id);
      this.pendingUpdates.add(pdar_id);
      this.notifySelectedPdarChanged();
    });
  }

  private startUpdateLoop() {
    const loop = () => {
      if (this.pendingUpdates.size > 0) {
        this.flushUpdates();
      }
      this.animationFrameId = requestAnimationFrame(loop);
    };
    this.animationFrameId = requestAnimationFrame(loop);
  }

  public updatePdarBatch(payload: PdarPayload) {
    const now = Date.now();

    // 🛰️ Platform reflects the current frame. A valid platform object shows /
    // updates the icon; an undefined, null or omitted platform hides it while
    // the sensors + AoAs below still render. NOTE: JSON drops `undefined` keys
    // on the wire, so an undefined platform arrives as an omitted key — we treat
    // "no valid platform" uniformly rather than relying on `"platform" in payload`.
    if (payload.platform) {
      this.platformCache = payload.platform;
      this.pendingUpdates.add(payload.platform.platform_id);
    } else {
      // Queue the outgoing platform id so the flush removes its feature.
      if (this.platformCache) {
        this.pendingUpdates.add(this.platformCache.platform_id);
      }
      this.platformCache = null;
    }

    // 🎯 Process Targets — merge by target_id so matching IDs update in place
    // and targets from earlier messages are preserved (never wiped). Stale
    // targets fall away via pruneExpiredPdars; explicit removal via pdar:remove.
    if (payload.targets && Array.isArray(payload.targets)) {
      for (const target of payload.targets) {
        target.timestamp = target.timestamp || now;
        this.targetCache.set(target.target_id, target);
        this.pendingUpdates.add(target.target_id);
      }
    }

    // 📡 Process Radar Sensors (Only mutate cache if explicitly provided)
    if (payload.pdars && Array.isArray(payload.pdars)) {
      for (const pdar of payload.pdars) {
        pdar.timestamp = pdar.timestamp || now;
        this.pdarCache.set(pdar.pdar_id, pdar);
        this.pendingUpdates.add(pdar.pdar_id);
      }
    }

    // Real-time stream update — mirrors AircraftManager's
    // "data:aircraft-batch-updated" emit, keeping usePdarSelection's
    // "allPdars" list in sync every tick regardless of selection activity.
    this.eventBus.emit("data:pdar-batch-updated", { payload });
  }

  // Pure mathematical translation to protect WebGL performance (Avoids full turf.js bundle overhead)
  private calculateDestination(
    lon: number,
    lat: number,
    bearingDegrees: number,
    distanceKm: number,
  ): [number, number] {
    const R = 6371; // Earth radius
    const brng = (bearingDegrees * Math.PI) / 180;
    const dR = distanceKm / R;

    const lat1 = (lat * Math.PI) / 180;
    const lon1 = (lon * Math.PI) / 180;

    const lat2 = Math.asin(
      Math.sin(lat1) * Math.cos(dR) +
        Math.cos(lat1) * Math.sin(dR) * Math.cos(brng),
    );
    const lon2 =
      lon1 +
      Math.atan2(
        Math.sin(brng) * Math.sin(dR) * Math.cos(lat1),
        Math.cos(dR) - Math.sin(lat1) * Math.sin(lat2),
      );

    return [
      (((lon2 * 180) / Math.PI + 540) % 360) - 180,
      (lat2 * 180) / Math.PI,
    ];
  }

  // Note: currentlySelectedIds/pendingUpdates/notify are also applied by the
  // pdar:selected/pdar:deselected listeners in setupListeners() once
  // `selection` emits below — set eagerly here too so getSelectedPdars() is
  // correct even if called synchronously before that event round-trips.
  public selectPdar(pdar_id: string) {
    if (!pdar_id) return;
    this.currentlySelectedIds.add(pdar_id);
    this.selection.selectAircraft(pdar_id);
    this.pendingUpdates.add(pdar_id);
  }

  public deselectPdar(pdar_id: string) {
    if (!pdar_id) return;
    this.currentlySelectedIds.delete(pdar_id);
    this.selection.deselectAircraft(pdar_id);
    this.pendingUpdates.add(pdar_id);
  }

  // Selection helper access — mirrors AircraftManager.getSelection(), lets
  // callers (e.g. usePdarSelection) reach PdarsSelection.toggleAircraft directly.
  public getSelection(): PdarsSelection {
    return this.selection;
  }

  // Fast membership check against the render-loop's selected-id set (kept in
  // sync with `selection` via the pdar:selected/pdar:deselected listeners in
  // setupListeners), mirroring AircraftManager.isSelected.
  public isSelected(pdar_id: string): boolean {
    return this.currentlySelectedIds.has(pdar_id);
  }

  // Resolves currently-selected ids to their full Pdars objects, mirroring
  // AircraftManager.getSelectedAircraft().
  public getSelectedPdars(): Pdars[] {
    const selected: Pdars[] = [];
    for (const id of this.currentlySelectedIds) {
      const pdar = this.pdarCache.get(id);
      if (pdar) selected.push(pdar);
    }
    return selected;
  }

  public notifySelectedPdarChanged() {
    this.eventBus.emit("pdar:selected-pdar-updated", {
      pdars: this.getSelectedPdars(),
    });
  }

  // Sets/clears a sensor's calibration offset (degrees, any range — will be
  // normalized to 0..360 when applied). Passing 0 stores an explicit 0
  // rather than deleting the entry, so getOrientationOffset stays accurate
  // even before the next render; queues a re-render so the icon/AoA lines
  // rotate immediately (including mid-drag on the calibration slider) and
  // notifies listeners (e.g. usePdarSelection) with the resolved, offset-
  // applied orientation for that sensor.
  public setOrientationOffset(pdar_id: string, offsetDeg: number) {
    if (!pdar_id) return;
    this.orientationOffsets.set(pdar_id, offsetDeg);
    this.pendingUpdates.add(pdar_id);
    this.notifyOrientationChanged(pdar_id);
  }

  public getOrientationOffset(pdar_id: string): number {
    return this.orientationOffsets.get(pdar_id) ?? 0;
  }

  // Resolved orientation actually applied to a sensor's icon/AoA lines —
  // wire `orientation` (0 if the sensor hasn't been seen yet) plus its
  // calibration offset, normalized to 0..360.
  public getResolvedOrientation(pdar_id: string): number {
    const station = this.pdarCache.get(pdar_id);
    const base = station?.orientation ?? 0;
    return to360(base + this.getOrientationOffset(pdar_id));
  }

  private notifyOrientationChanged(pdar_id: string) {
    this.eventBus.emit("pdar:orientation-updated", {
      pdar_id,
      orientation_deg: this.getResolvedOrientation(pdar_id),
    });
  }

  // Explicitly clears a single PDAR sensor (and its AoA lines) off the map.
  // Driven by the "pdar:remove" topic.
  public removePdar(pdar_id: string) {
    const wasSelected = this.currentlySelectedIds.has(pdar_id);
    this.pdarCache.delete(pdar_id);
    this.currentlySelectedIds.delete(pdar_id);
    this.pendingUpdates.add(pdar_id);

    if (this.selection.isSelected(pdar_id)) {
      this.selection.deselectAircraft(pdar_id);
    }

    if (wasSelected) this.notifySelectedPdarChanged();
  }

  private async flushUpdates() {
    if (!this.map || !this.map.isStyleLoaded()) return;

    const pdarSource = this.map.getSource(SOURCES.PDAR) as GeoJSONSource;
    const aoaSource = this.map.getSource(
      SOURCES.PDAR_AOA || "pdar-aoa-source",
    ) as GeoJSONSource;

    if (!pdarSource) return;
    this.pendingUpdates.clear();

    if (
      !this.platformCache &&
      this.pdarCache.size === 0 &&
      this.targetCache.size === 0
    ) {
      try {
        pdarSource.setData({ type: "FeatureCollection", features: [] });
        if (aoaSource)
          aoaSource.setData({ type: "FeatureCollection", features: [] });
      } catch (err) {
        console.warn("MapLibre PDAR canvas flush deferred:", err);
      }
      return;
    }

    const compiledFeatures: GeoJSON.Feature[] = [];
    const compiledAoaLines: GeoJSON.Feature[] = [];

    // ─── PART 1: PLATFORM ───────────────────────────────────────────────────
    if (this.platformCache) {
      const isSel = this.currentlySelectedIds.has(
        this.platformCache.platform_id,
      );
      const markerKey = this.renderer.getPlatformSpriteKey(
        this.platformCache,
        isSel,
      );

      compiledFeatures.push({
        id: this.platformCache.platform_id,
        type: "Feature",
        geometry: {
          type: "Point",
          coordinates: [
            this.platformCache.position.lon,
            this.platformCache.position.lat,
          ],
        },
        properties: {
          pdar_id: this.platformCache.platform_id,
          entity_type: "platform",
          markerImage: markerKey,
          type: this.platformCache.type,
          category: this.platformCache.category,
          heading_deg: this.platformCache.heading_deg ?? 0,
          speed_knots: this.platformCache.speed_knots ?? 0,
          isSelected: isSel,
        },
      });
    }

    // ─── PART 2: TARGETS ────────────────────────────────────────────────────
    for (const [target_id, target] of this.targetCache) {
      const isSel = this.currentlySelectedIds.has(target_id);
      const markerKey = this.renderer.getTargetSpriteKey(target, isSel);

      compiledFeatures.push({
        id: target_id,
        type: "Feature",
        geometry: {
          type: "Point",
          coordinates: [target.position.lon, target.position.lat],
        },
        properties: {
          pdar_id: target_id,
          entity_type: "target",
          markerImage: markerKey,
          lastSeen: target.timestamp,
          type: target.type || "drone",
          category: target.category || "drone",
          altitude_ft: getAltitudeInFeet(target.altitude_ft ?? 0),
          heading_deg: target.heading_deg ?? 0,
          relative_bearing: getHeadingInDegrees(target.relative_bearing ?? 0),
          magnetic_bearing: getHeadingInDegrees(target.magnetic_bearing ?? 0),
          speed_knots: getSpeedInKnots(target.speed_knots ?? 0),
          intent: target.intent || "unknown",
          isSelected: isSel,
        },
      });
    }

    // ─── PART 3: SENSORS & AOA LINE ENGINE ──────────────────────────────────
    for (const [pdar_id, station] of this.pdarCache) {
      const isSel = this.currentlySelectedIds.has(pdar_id);
      const markerKey = this.renderer.getSensorSpriteKey(station, isSel);

      const sensorLon = station.position.lon;
      const sensorLat = station.position.lat;

      // Wire orientation + client-side calibration offset, normalized —
      // this is what the icon actually rotates to and what every AoA
      // bearing below is offset by, so a PDAR that isn't physically
      // true-north-facing can be corrected for from the UI.
      const orientationOffset = this.orientationOffsets.get(pdar_id) ?? 0;
      const resolvedOrientation = to360(
        station.orientation + orientationOffset,
      );

      compiledFeatures.push({
        id: pdar_id,
        type: "Feature",
        geometry: {
          type: "Point",
          coordinates: [sensorLon, sensorLat],
        },
        properties: {
          pdar_id: station.pdar_id,
          entity_type: "sensor",
          markerImage: markerKey,
          lastSeen: station.timestamp,
          health_state: station.health_state,
          clockPlacement: station.clockPlacement,
          orientation: station.orientation,
          orientation_offset: orientationOffset,
          // Consumed by layers/pdars.ts's icon-rotate for sensor features.
          orientation_deg: resolvedOrientation,
          location: station.location,
          aoas_count: station.aoas.length,
          aoas: JSON.stringify(station.aoas),
          isSelected: isSel,
        },
      });

      // 📡 Process individual AoA lines embedded within this specific sensor
      if (station.aoas && Array.isArray(station.aoas)) {
        station.aoas.forEach((aoa, index) => {
          const rawBearing =
            typeof aoa === "object" ? (aoa.angle ?? aoa.bearing_deg) : aoa;
          const health =
            typeof aoa === "object" ? aoa.health_state : station.health_state;

          if (typeof rawBearing !== "number") return;

          // Offset each AoA bearing by the same calibration applied to the
          // sensor's own icon, so the lines rotate in lockstep with it.
          const bearing = to360(rawBearing + orientationOffset);

          // Per-line length (metres); falls back to the 3000m default.
          const distanceMeters =
            typeof aoa === "object" && typeof aoa.distance_meters === "number"
              ? aoa.distance_meters
              : DEFAULT_AOA_LINE_LENGTH_M;

          const endCoords = this.calculateDestination(
            sensorLon,
            sensorLat,
            bearing,
            distanceMeters / 1000,
          );

          compiledAoaLines.push({
            type: "Feature",
            id: `${pdar_id}-aoa-${index}`,
            geometry: {
              type: "LineString",
              coordinates: [[sensorLon, sensorLat], endCoords],
            },
            properties: {
              sensor_id: pdar_id,
              bearing_deg: bearing,
              health_state: health || "nominal",
              isSensorSelected: isSel,
              // Optional per-line style overrides (fall back to health/selection
              // styling in the layer when absent).
              color: typeof aoa === "object" ? (aoa.color ?? null) : null,
              opacity: typeof aoa === "object" ? (aoa.opacity ?? null) : null,
            },
          });
        });
      }
    }

    // Push coordinates to the WebGL rendering pipelines
    try {
      pdarSource.setData({
        type: "FeatureCollection",
        features: compiledFeatures,
      });

      if (aoaSource) {
        aoaSource.setData({
          type: "FeatureCollection",
          features: compiledAoaLines,
        });
      }
    } catch (err) {
      console.warn("MapLibre PDAR layer synchronization deferred:", err);
    }

    this.pruneExpiredPdars();
  }

  private pruneExpiredPdars() {
    const now = Date.now();

    for (const [pdar_id, station] of this.pdarCache) {
      if (this.currentlySelectedIds.has(pdar_id)) continue;
      const lastSeen = station.timestamp ?? 0;
      if (now - lastSeen > STALE_AFTER_MS) {
        this.removePdar(pdar_id);
      }
    }

    // Targets share the same staleness lifecycle so accumulated tracks that
    // stop updating fall away instead of lingering on the canvas forever.
    for (const [target_id, target] of this.targetCache) {
      if (this.currentlySelectedIds.has(target_id)) continue;
      const lastSeen = target.timestamp ?? 0;
      if (now - lastSeen > STALE_AFTER_MS) {
        this.targetCache.delete(target_id);
        this.pendingUpdates.add(target_id);
      }
    }
  }

  public getPdarCount(): number {
    return this.pdarCache.size;
  }
  public getAllPdars(): Map<string, Pdars> {
    return this.pdarCache;
  }

  public destroy() {
    if (this.animationFrameId) cancelAnimationFrame(this.animationFrameId);
    this.selection.destroy();
    this.renderer.destroy();
    this.pdarCache.clear();
    this.targetCache.clear();
    this.platformCache = null;
    this.pendingUpdates.clear();
    this.currentlySelectedIds.clear();
    this.orientationOffsets.clear();
  }
}
